import {effect, inject, Injectable, Injector, runInInjectionContext, signal, WritableSignal} from '@angular/core';
import typia from "typia";
import {
  FormCurrentHouseholdMember,
  FormHouseholdMember
} from "../pages/update-profile-section/update-household-members-info/update-household-members-form.service";
import {
  HouseholdAssetsInfo,
  HouseholdIncomeInfo
} from "../pages/update-profile-section/update-income-assets-info/update-assets-income-form.service";
import {
  AddressInfoFormData
} from "../pages/update-profile-section/update-address-info/update-address-info-form.service";
import {Preferences} from '@capacitor/preferences';
import {SignupFormState} from "../pages/signup/signup-form.service";
import {User} from "./user.service";
import {ExternalCode} from "./external-code.service";
import {FederatedAuthPlatform, FederatedAuthTokens} from "./auth/federated-auth.service";
import {OAuthState} from "./auth/auth.service";

type Is<T> = (value: unknown) => value is T;
type TypeFromTypia<T> = T extends Is<infer U> ? U : never;


const keyTypeMapping = {
  CURRENT_HOUSEHOLD_MEMBER_KEY: typia.is<FormCurrentHouseholdMember>,
  CURRENT_HOUSEHOLD_MEMBERS_KEY: typia.is<FormHouseholdMember[]>,
  HOUSEHOLD_ASSETS: typia.is<HouseholdAssetsInfo>,
  HOUSEHOLD_INCOME: typia.is<HouseholdIncomeInfo>,
  ADDRESS_INFO: typia.is<AddressInfoFormData>,
  AUTH_TOKENS: typia.is<FederatedAuthTokens>,
  AUTH_TYPE: typia.is<FederatedAuthPlatform | 'username-password'>,
  LAST_USER_STATUS: typia.is<User['status']>,
  LAST_USER_ID: typia.is<string>,
  SIGNUP_FORM_STATE: typia.is<SignupFormState>,
  EXTERNAL_CODE: typia.is<ExternalCode>,
  REFERRAL_POPUP_COUNT: typia.is<number>,
  BILLING_MODE: typia.is<'monthly' | 'annual'>,
  PROMOTION: typia.is<boolean>,
  OATH_STATE: typia.is<OAuthState>,
  HOMEY_EXPLANATION_SHOWN: typia.is<boolean>,
  APPLICATION_GUIDE_SHOWN: typia.is<boolean>,
  REQUESTED_INITIAL_PUSH_PERMISSION: typia.is<boolean>,
  PAST_DUE_FLOW_STARTED: typia.is<boolean>,
  STATUS_UPDATE_NOTIFICATION_LAST_SHOWN: typia.is<string>,
  STATUS_UPDATE_NOTIFICATION_ACTIVATED_DATE: typia.is<string>,
} as const;

export type StorageKey = keyof typeof keyTypeMapping;

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private readonly injector = inject(Injector);

  async setItem<T extends StorageKey>(key: T, value: TypeFromTypia<typeof keyTypeMapping[T]>) {
    await Preferences.set({
      key,
      value: JSON.stringify(value),
    });
  }

  async updateItem<T extends StorageKey>(key: T, updateFn: (value: TypeFromTypia<typeof keyTypeMapping[T]> | undefined) => TypeFromTypia<typeof keyTypeMapping[T]>) {
    const item = await this.getItem(key);

    await this.setItem(key, updateFn(item));
  }

  async getItem<T extends StorageKey>(key: T): Promise<TypeFromTypia<typeof keyTypeMapping[T]> | undefined> {
    const item = await Preferences.get({key});

    if (item && item.value) {
      const parsed = JSON.parse(item.value);
      if (keyTypeMapping[key](parsed)) {
        return parsed as TypeFromTypia<typeof keyTypeMapping[T]>;
      } else {
        console.error(`Error parsing item from sessionStorage with key: ${key}`);
        await Preferences.remove({key});
      }
    }

    return undefined;
  }

  generateItemSignal<T extends StorageKey>(key: T, initialValue: TypeFromTypia<typeof keyTypeMapping[T]>): WritableSignal<TypeFromTypia<typeof keyTypeMapping[T]>> {
    return runInInjectionContext(this.injector, () => {
      const val = signal(initialValue);

      this.getItem(key).then(
        (value) => {
          if (value) {
            val.set(value);
          }
        }
      )

      effect(() => {
        const value = val();

        if (value !== initialValue) {
          this.setItem(key, value);
        }
      });

      return val;
    })
  }

  async removeItem<T extends StorageKey>(key: T) {
    await Preferences.remove({key});
  }
}
